<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { chatGptActions, chatGptUrl } from 'dashboard/helper/chatTranscript';

const props = defineProps({
  currentChat: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const textareaRef = ref(null);
const transcript = ref('');
const stats = ref(null);
const isLoading = ref(true);
const includeNotes = ref(false);

// Company by inbox-name suffix (-MK / -S3), the fork's convention.
const isS3 = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](props.currentChat.inbox_id);
  return /[-\s]S3$/i.test((inbox?.name || '').trim());
});
const actions = computed(() => chatGptActions(isS3.value));
const actionLabels = computed(() => ({
  ALL: t('CONTACT_PANEL.OPEN_IN_CHATGPT_ACTIONS.ALL'),
  SUMMARIZE: t('CONTACT_PANEL.OPEN_IN_CHATGPT_ACTIONS.SUMMARIZE'),
  DRAFT_REPLY: t('CONTACT_PANEL.OPEN_IN_CHATGPT_ACTIONS.DRAFT_REPLY'),
  RED_FLAGS: t('CONTACT_PANEL.OPEN_IN_CHATGPT_ACTIONS.RED_FLAGS'),
}));

// One server request builds the whole transcript (ordered, noise-free,
// masked, Smith3D facts on top); a second only if notes are toggled.
const loadTranscript = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.getTranscript(
      props.currentChat.id,
      includeNotes.value
    );
    transcript.value = data.text;
    stats.value = data;
  } catch {
    useAlert(t('CONTACT_PANEL.OPEN_IN_CHATGPT_ERROR'));
    dialogRef.value.close();
  } finally {
    isLoading.value = false;
  }
};

watch(includeNotes, loadTranscript);

onMounted(() => {
  dialogRef.value.open();
  loadTranscript();
});

// Copy + open stay synchronous inside the click: async clipboard writes can
// fail once the new tab takes focus, and Safari blocks window.open after an
// await. The preview textarea doubles as the copy source.
const runAction = action => {
  const text = `${action.prompt}\n\n${transcript.value}`;
  const textarea = textareaRef.value;
  textarea.value = text;
  textarea.select();
  textarea.setSelectionRange(0, text.length); // iOS
  document.execCommand('copy');
  const { url, prefilled } = chatGptUrl(text);
  window.open(url, '_blank', 'noopener,noreferrer');
  useAlert(
    prefilled
      ? t('CONTACT_PANEL.OPEN_IN_CHATGPT_SENT')
      : t('CONTACT_PANEL.OPEN_IN_CHATGPT_PASTE')
  );
  dialogRef.value.close();
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACT_PANEL.OPEN_IN_CHATGPT')"
    :description="t('CONTACT_PANEL.OPEN_IN_CHATGPT_DESC')"
    width="xl"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-2">
      <textarea
        ref="textareaRef"
        readonly
        rows="10"
        class="w-full !mb-0 text-xs font-mono resize-none"
        :value="
          isLoading ? t('CONTACT_PANEL.OPEN_IN_CHATGPT_LOADING') : transcript
        "
      />
      <div class="flex items-center justify-between gap-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="includeNotes" type="checkbox" :disabled="isLoading" />
          {{ t('CONTACT_PANEL.OPEN_IN_CHATGPT_INCLUDE_NOTES') }}
        </label>
        <span v-if="stats && !isLoading" class="text-xs text-n-slate-11">
          {{
            stats.omitted_count
              ? t('CONTACT_PANEL.OPEN_IN_CHATGPT_STATS_TRIMMED', {
                  count: stats.message_count,
                  omitted: stats.omitted_count,
                })
              : t('CONTACT_PANEL.OPEN_IN_CHATGPT_STATS', {
                  count: stats.message_count,
                })
          }}
        </span>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-wrap items-center justify-end w-full gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="dialogRef.close()"
        />
        <NextButton
          v-for="(action, index) in actions"
          :key="action.key"
          type="button"
          :faded="index > 0 && actions.length > 1"
          :label="actionLabels[action.labelKey]"
          :disabled="isLoading || !transcript"
          @click="runAction(action)"
        />
      </div>
    </template>
  </Dialog>
</template>
